import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { Plus, Pencil, CornerDownRight, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { AddCategory } from "./AddCategory";
import { ExportModal } from "@/components/common/Model/ExportModal";
import {
  ICategory,
  ICategoryResponse,
} from "@/interfaces/Category/category.interface";
import { IFilter, IPageInfo } from "@/interfaces/common.interface";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import CommonTable, {
  Action,
  TableColumn,
} from "@/components/common/CommonTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { GetFilterUrl, showToastNotification, downloadExcel } from "@/utility/helper";
import { useFilter } from "@/hooks/useFilter";
import Pagination from "@/components/common/Pagination";
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { TableFilters } from "@/components/common/TableFilters";
import { CommonSelect } from "@/components/common/CommonSelect";
import { TableItemIdentity } from "@/components/common/TableItemIdentity";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";

export const Category = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "addSub">(
    "create",
  );
  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(
    null,
  );
  const { canCreate, canEdit, canExport } = usePermissions();
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string | number>>(
    new Set(),
  );
  const { filters, debouncedFilters, updateFilter, handlePageChange, handlePageSizeChange } =
    useFilter<IFilter>({
      search: "",
      status: "",
      sort: "",
      page: 1,
      limit: 10,
      topLevelOnly: true,
    });

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);
  // const [showExportModal, setShowExportModal] = useState(false);

  const handleExport = async () => {
    const queryParams = GetFilterUrl({
      status: filters.status,
      startDate: filters.startDate,
      endDate: filters.endDate,
      sort: filters.sort
    });
    const url = `${API_URL + API_ENDPOINT.categories}/export/excel?${queryParams}`;
    setLoadingExport(true);
    try {
      await downloadExcel(url, 'categories_export.xlsx');
    } catch (error) {
      ThrowError(error);
    } finally {
      setLoadingExport(false);
    }
  };

  const handleDateChange = (newRange: any) => {
    // If it's a callback (React state setter), we need to resolve it, though DateRangePicker mostly passes the array directly.
    const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
    setDateRange(resolvedRange);

    const start = resolvedRange[0]?.startDate;
    const end = resolvedRange[0]?.endDate;
    const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
    const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

    if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
      updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
    }
  };

  const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
    limit: 10,
    page: 1,
    total: 0,
    totalPages: 0,
  });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const queryParams = GetFilterUrl(debouncedFilters);

      const response = await callApi<ICategoryResponse>(
        `${API_URL + API_ENDPOINT.categories}?${queryParams.toString()}`,
        { method: "GET" },
      );
      setCategories(response?.data?.categories || []);
      setPageMetadata(
        response?.data?.pageInfo || {
          limit: 10,
          page: 1,
          total: 0,
          totalPages: 0,
        },
      );
    } catch (error) {
      ThrowError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (
    item: ICategory,
    isSubCategory: boolean = false,
    parentId?: string,
  ) => {
    setTogglingId(item.id);
    const newStatus = item.status === "active" ? "inactive" : "active";
    try {
      const url = `${API_URL + API_ENDPOINT.categories}/${item.id}`;
      const formData = new FormData();
      formData.append("status", newStatus);

      await callApi(url, {
        method: "PATCH",
        multipart: true,
        data: formData,
      });

      showToastNotification(
        "success",
        `${isSubCategory ? "Sub-category" : "Category"} status updated successfully`,
      );

      // Optimistic Update
      setCategories((prev) =>
        prev.map((cat) => {
          if (isSubCategory && cat.id === parentId) {
            return {
              ...cat,
              subcategories: cat.subcategories?.map((sub) =>
                sub.id === item.id ? { ...sub, status: newStatus } : sub,
              ),
            };
          }
          if (!isSubCategory && cat.id === item.id) {
            return { ...cat, status: newStatus };
          }
          return cat;
        }),
      );
    } catch (error) {
      ThrowError(error);
    } finally {
      setTogglingId(null);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [
    debouncedFilters.search,
    debouncedFilters.status,
    debouncedFilters.sort,
    debouncedFilters.page,
    debouncedFilters.limit,
    debouncedFilters.startDate,
    debouncedFilters.endDate,
  ]);

  const toggleExpand = (item: ICategory) => {
    const newKeys = new Set(expandedRowKeys);
    if (newKeys.has(item.id)) {
      newKeys.delete(item.id);
    } else {
      newKeys.add(item.id);
    }
    setExpandedRowKeys(newKeys);
  };

  const columns: TableColumn<ICategory>[] = [
    {
      key: "name",
      header: "Category",
      render: (item) => (
        <TableItemIdentity
          image={item.media?.url}
          name={item.name}
          // subtitle="Main Category"
          fallbackText="No Img"
        />
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (item) => (
        <div
          className="max-w-[300px] truncate text-muted-foreground"
          title={item.description}
        >
          {item.description}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <div className="flex items-center gap-3">
          <Switch
            checked={item.status === "active"}
            onCheckedChange={() => handleToggleStatus(item)}
            disabled={togglingId === item.id}
            className="data-[state=checked]:bg-green-500"
          />
          <span
            className={`text-[10px] font-bold uppercase tracking-wider ${item.status === "active"
              ? "text-green-600"
              : "text-muted-foreground"
              }`}
          >
            {togglingId === item.id ? "Updating..." : item.status}
          </span>
        </div>
      ),
    },
  ];

  const actions: Action<ICategory>[] = [
    ...(canCreate(RESOURCES.CATEGORY) ? [{
      text: "Add Sub Category",
      icon: (
        <Plus
          strokeWidth={1.5}
          size={16}
          className="text-red-500 cursor-pointer"
        />
      ),
      onClick: (record: ICategory) => {
        setSelectedCategory(record);
        setModalMode("addSub");
        setShowAddModal(true);
      },
    }] : []),
    ...(canEdit(RESOURCES.CATEGORY) ? [{
      text: "Edit",
      icon: <Pencil strokeWidth={1.5} size={16} className="cursor-pointer" />,
      onClick: (record: ICategory) => {
        setSelectedCategory(record);
        setModalMode("edit");
        setShowAddModal(true);
      }
    }] : []),
  ];

  return (
    <Layout>
      <MainHeading
        title="Category"
        description={`${pageMetadata.total} Categories Total · ${categories.length} items on current page`}
        actionLabel={canCreate(RESOURCES.CATEGORY) && "Add Category"}
        actionIcon={canCreate(RESOURCES.CATEGORY) && <Plus className="w-4 h-4" />}
        onAction={() => {
          setSelectedCategory(null);
          setModalMode("create");
          setShowAddModal(true);
        }}
      ></MainHeading>

      <div className="mt-6 flex flex-col gap-4">
        <TableFilters
          searchTerm={filters.search}
          onSearchChange={(val) => updateFilter({ search: val })}
          placeholder="Search categories..."
        >
          <div className="flex items-center gap-3 flex-wrap">
            <DateRangePicker
              dateRange={dateRange}
              setDateRange={handleDateChange as any}
            />
            <CommonSelect
              value={filters.status}
              onValueChange={(val) =>
                updateFilter({ status: val })
              }
              options={[
                // { value: "", label: "All Categories" },
                { value: "active", label: "Active Only", color: "bg-green-500" },
                {
                  value: "inactive",
                  label: "Inactive Only",
                  color: "bg-primary",
                },
              ]}
              placeholder="Select Status"
              triggerClassName="w-[180px] h-9 rounded-lg border-secondary/20 bg-secondary/5 font-semibold"
            />
            <CommonSelect
              value={filters.sort}
              onValueChange={(val) => updateFilter({ sort: val })}
              options={[
                { value: "newest", label: "Newest First" },
                { value: "oldest", label: "Oldest First" },
              ]}
              triggerClassName="w-[160px] h-10 rounded-xl border-secondary/20 bg-card font-semibold"
              placeholder="Sort By"
            />
            {canExport(RESOURCES.CATEGORY) && <Button
              variant="outline"
              onClick={() => handleExport()}
              className="h-10 px-4 flex items-center gap-2 rounded-xl"
              leftIcon={<Download className="w-4 h-4" />}
              isLoading={loadingExport}
            >
              Export
            </Button>}
          </div>
        </TableFilters>
        <CommonTable
          columns={columns}
          data={categories}
          loading={loading}
          expandable
          expandedRowKeys={expandedRowKeys}
          onToggleExpand={toggleExpand}
          renderExpandedRow={(cat) => (
            <div className="px-12 py-2 space-y-2">
              {cat.subcategories && cat.subcategories.length > 0 ? (
                cat.subcategories.map((sub) => (
                  <div
                    key={sub.id}
                    className="flex items-center gap-4 text-sm text-muted-foreground bg-secondary/5 border border-secondary/10 p-2 rounded-xl hover:bg-secondary/10 transition-all group"
                  >
                    <CornerDownRight size={16} strokeWidth={1.5} />
                    <TableItemIdentity
                      image={API_URL + "/" + sub.media?.url}
                      name={sub.name}
                      subtitle="Sub Category"
                      fallbackText={sub.name.charAt(0)}
                      containerClassName="min-w-[200px]"
                    />

                    <div className="ml-auto flex items-center gap-4">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={sub.status === "active"}
                          onCheckedChange={() =>
                            handleToggleStatus(sub, true, cat.id)
                          }
                          disabled={togglingId === sub.id}
                          className="scale-90"
                        />
                        <StatusBadge status={sub.status} />
                      </div>
                      {canEdit(RESOURCES.CATEGORY) && <button
                        onClick={() => {
                          setSelectedCategory(sub);
                          setModalMode("edit");
                          setShowAddModal(true);
                        }}
                        className="p-1.5 rounded-md hover:bg-primary/10 text-primary transition-colors"
                        title="Edit Subcategory"
                      >
                        <Pencil size={14} />
                      </button>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-xs text-muted-foreground italic pl-2">
                  No subcategories available
                </div>
              )}
            </div>
          )}
          actions={actions}
          emptyMessage="No categories found. Add your first category!"
        />
        <Pagination
          currentPage={filters.page || 1}
          totalPages={pageMetadata.totalPages}
          totalItems={pageMetadata.total}
          itemsPerPage={pageMetadata.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      <AddCategory
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        modalMode={modalMode}
        selectedCategory={selectedCategory}
        onSuccess={fetchCategories}
      />

      {/* <ExportModal
        open={showExportModal}
        onOpenChange={setShowExportModal}
        title="Export Categories"
        onExport={handleExport}
        statusOptions={[
          { label: 'All Status', value: '' },
          { label: 'Active', value: 'active' },
          { label: 'Inactive', value: 'inactive' },
        ]}
      /> */}
    </Layout>
  );
};
